// TranslationSection.js — Language Solar System
// Languages orbit a central glowing orb in 3D CSS space, mouse tilts the system.
// Sticky-scroll pattern: 220vh outer, 100vh sticky inner.
import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  AnimatePresence,
} from 'framer-motion';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const ORBIT_LANGS = [
  // Inner orbit (orbit 0, radius 140px, period 12s) — 4 langs
  { text: 'ESPAÑOL',   orbit: 0, idx: 0, period: 12 },
  { text: 'FRANÇAIS',  orbit: 0, idx: 1, period: 12 },
  { text: '日本語',    orbit: 0, idx: 2, period: 12 },
  { text: 'DEUTSCH',   orbit: 0, idx: 3, period: 12 },
  // Middle orbit (orbit 1, radius 220px, period 20s) — 5 langs
  { text: 'العربية',   orbit: 1, idx: 0, period: 20 },
  { text: 'PORTUGUÊS', orbit: 1, idx: 1, period: 20 },
  { text: '한국어',    orbit: 1, idx: 2, period: 20 },
  { text: 'ITALIANO',  orbit: 1, idx: 3, period: 20 },
  { text: 'РУССКИЙ',   orbit: 1, idx: 4, period: 20 },
  // Outer orbit (orbit 2, radius 320px, period 30s) — 6 langs
  { text: 'TÜRKÇE',    orbit: 2, idx: 0, period: 30 },
  { text: 'हिन्दी',   orbit: 2, idx: 1, period: 30 },
  { text: '中文',      orbit: 2, idx: 2, period: 30 },
  { text: 'POLSKI',    orbit: 2, idx: 3, period: 30 },
  { text: 'SVENSKA',   orbit: 2, idx: 4, period: 30 },
  { text: 'BAHASA',    orbit: 2, idx: 5, period: 30 },
];

const ORBIT_RADII  = [140, 220, 320];
const ORBIT_COUNTS = [4, 5, 6]; // langs per orbit
const ORBIT_COLORS = [
  'rgba(255,255,255,0.75)',
  'rgba(255,255,255,0.55)',
  'rgba(255,255,255,0.35)',
];
const ORBIT_FONT_SIZES = ['10px', '9.5px', '9px'];

// Inject @keyframes for each unique period once
const PERIODS = [12, 20, 30];

function injectKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('orbit-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'orbit-keyframes';
  style.textContent = PERIODS.map(
    period => `
    @keyframes orbit-${period} {
      from { transform: var(--orbit-start); }
      to   { transform: var(--orbit-end); }
    }
  `
  ).join('\n');
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Concentric ring pulse for the central orb
function OrbRings({ absorbed }) {
  const rings = [
    { delay: 0,    duration: 2.4 },
    { delay: 0.6,  duration: 2.4 },
    { delay: 1.2,  duration: 2.4 },
    { delay: 1.8,  duration: 2.4 },
  ];
  const brightness = 0.6 + (absorbed / 15) * 0.4;

  return (
    <>
      {rings.map((r, i) => (
        <motion.div
          key={i}
          animate={{
            scale:   [1, 1.4, 1.8, 2.2],
            opacity: [0.4 * brightness, 0.2 * brightness, 0.08 * brightness, 0],
          }}
          transition={{
            duration: r.duration,
            repeat: Infinity,
            delay: r.delay,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.18)',
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
}

// Signal ripple emit from orb
function SignalRipple() {
  return (
    <motion.div
      initial={{ scale: 0.5, opacity: 0.5 }}
      animate={{ scale: 3.5, opacity: 0 }}
      transition={{ duration: 1.8, ease: 'easeOut' }}
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        pointerEvents: 'none',
      }}
    />
  );
}

// Language node on an orbit
function LangNode({ lang, absorbed }) {
  const orbitRadius = ORBIT_RADII[lang.orbit];
  const orbitCount  = ORBIT_COUNTS[lang.orbit];
  const startAngle  = (lang.idx / orbitCount) * 360;
  const period      = lang.period;

  // CSS custom props hold the start/end transform strings
  const orbitStart = `rotate(${startAngle}deg) translateX(${orbitRadius}px) rotate(${-startAngle}deg)`;
  const orbitEnd   = `rotate(${startAngle + 360}deg) translateX(${orbitRadius}px) rotate(${-startAngle - 360}deg)`;

  const isAbsorbed = lang.orbit * 10 + lang.idx < absorbed;

  return (
    <motion.div
      animate={
        isAbsorbed
          ? { scale: 0, opacity: 0, x: 0, y: 0 }
          : { scale: 1, opacity: 1 }
      }
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 3D tilt: outer ring gets subtle extra rotateX
        transform: lang.orbit === 2 ? 'rotateX(15deg)' : undefined,
        transformStyle: 'preserve-3d',
      }}
    >
      <div
        style={{
          animation: `orbit-${period} ${period}s linear infinite`,
          '--orbit-start': orbitStart,
          '--orbit-end':   orbitEnd,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Dot indicator */}
        <div
          style={{
            position: 'absolute',
            width: 3,
            height: 3,
            borderRadius: '50%',
            background: ORBIT_COLORS[lang.orbit],
            top: -1.5,
            left: -1.5,
          }}
        />
        {/* Text label */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: ORBIT_FONT_SIZES[lang.orbit],
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: ORBIT_COLORS[lang.orbit],
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          {lang.text}
        </div>
      </div>
    </motion.div>
  );
}

// Ellipse ring outlines (decorative)
function OrbitRing({ radius, orbit }) {
  const size = radius * 2;
  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: size,
        height: size,
        marginLeft: -radius,
        marginTop:  -radius,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.05)',
        transform: orbit === 2 ? 'rotateX(15deg)' : undefined,
        transformStyle: 'preserve-3d',
        pointerEvents: 'none',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Main Section
// ---------------------------------------------------------------------------
export default function TranslationSection() {
  const ref         = useRef(null);
  const containerRef = useRef(null);
  const [ripples, setRipples]       = useState([]);
  const [rippleKey, setRippleKey]   = useState(0);
  const [absorbed, setAbsorbed]     = useState(0);
  const [mouseX, setMouseX]         = useState(0.5);
  const [mouseY, setMouseY]         = useState(0.5);

  // Inject CSS keyframes on mount
  useEffect(() => { injectKeyframes(); }, []);

  // Scroll progress
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  // Absorbed count from scroll progress 0.4→0.8
  useEffect(() => {
    const unsub = scrollYProgress.on('change', v => {
      const t = Math.max(0, Math.min(1, (v - 0.4) / 0.4));
      setAbsorbed(Math.round(t * 15));
    });
    return unsub;
  }, [scrollYProgress]);

  // Mouse tilt springs
  const rawRotateX = useSpring(0, { stiffness: 60, damping: 20 });
  const rawRotateY = useSpring(0, { stiffness: 60, damping: 20 });

  const handleMouseMove = useCallback(e => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top)  / rect.height;
    setMouseX(nx);
    setMouseY(ny);
    rawRotateX.set((ny - 0.5) * -24);   // -12 to +12
    rawRotateY.set((nx - 0.5) *  36);   //  -18 to +18
  }, [rawRotateX, rawRotateY]);

  const handleMouseLeave = useCallback(() => {
    rawRotateX.set(0);
    rawRotateY.set(0);
  }, [rawRotateX, rawRotateY]);

  // Signal ripples every 2.5s
  useEffect(() => {
    const interval = setInterval(() => {
      setRippleKey(k => k + 1);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Orb brightness
  const orbBrightness = 0.6 + (absorbed / 15) * 0.4;
  const orbShadow = `0 0 ${40 + absorbed * 2}px rgba(255,255,255,${0.12 * orbBrightness}), 0 0 ${80 + absorbed * 3}px rgba(255,255,255,${0.06 * orbBrightness})`;

  // Text fade-in from scroll
  const textOpacity = useTransform(scrollYProgress, [0, 0.15], [0, 1]);

  return (
    <Box
      ref={ref}
      component="section"
      sx={{ height: '140vh', position: 'relative', background: '#000' }}
    >
      <Box
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          background: '#000',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'none',
        }}
      >
        {/* Section label */}
        <Typography
          sx={{
            position: 'absolute',
            top: 32,
            left: 40,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '11px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.25)',
            zIndex: 10,
          }}
        >
          04 — TRANSLATION
        </Typography>

        {/* Bottom-left text */}
        <motion.div
          style={{
            position: 'absolute',
            bottom: 60,
            left: 40,
            opacity: textOpacity,
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(28px, 4vw, 48px)',
              color: 'rgba(255,255,255,0.92)',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            32 languages.
          </div>
          <div
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(18px, 2.5vw, 28px)',
              color: 'rgba(255,255,255,0.28)',
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
              marginTop: 4,
            }}
          >
            One AI core.
          </div>
        </motion.div>

        {/* Absorbed counter */}
        <AnimatePresence>
          {absorbed > 0 && (
            <motion.div
              key="absorbed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                top: 32,
                right: 40,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '11px',
                letterSpacing: '0.18em',
                color: 'rgba(255,255,255,0.3)',
                zIndex: 10,
              }}
            >
              {absorbed} / 15 ABSORBED
            </motion.div>
          )}
        </AnimatePresence>

        {/* 3D perspective wrapper */}
        <Box sx={{ perspective: '1200px', perspectiveOrigin: '50% 50%' }}>
          <motion.div
            style={{
              rotateX: rawRotateX,
              rotateY: rawRotateY,
              transformStyle: 'preserve-3d',
              position: 'relative',
              width: 800,
              height: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Orbit ring outlines */}
            {ORBIT_RADII.map((r, i) => (
              <OrbitRing key={i} radius={r} orbit={i} />
            ))}

            {/* Language nodes */}
            {ORBIT_LANGS.map((lang, i) => (
              <LangNode key={`${lang.text}-${i}`} lang={lang} absorbed={absorbed} />
            ))}

            {/* Central orb */}
            <Box
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background:
                  'radial-gradient(circle at 38% 38%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 30%, rgba(255,255,255,0.15) 70%, rgba(255,255,255,0.04) 100%)',
                boxShadow: orbShadow,
                opacity: orbBrightness,
                flexShrink: 0,
                zIndex: 5,
              }}
            >
              {/* Concentric pulse rings */}
              <OrbRings absorbed={absorbed} />

              {/* Signal ripple — key change triggers new animation */}
              <SignalRipple key={rippleKey} />
            </Box>
          </motion.div>
        </Box>

        {/* Subtle vignette */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 70% 70% at 50% 50%, transparent 40%, rgba(0,0,0,0.7) 100%)',
            pointerEvents: 'none',
            zIndex: 6,
          }}
        />
      </Box>
    </Box>
  );
}
